// АРИФМЕТИКА СПИСКА ЭТАПОВ — то, что ряд печатает о себе. Порт функций борда `Stage Gate v5`.
//
// ИСТОЧНИК — `docs/design/wizard-s2/Life Map - s2 Stage Gate v6.html`, его `<script>`: `mo()`,
// `dur()`, `endOf()`, тег ряда из `rows()` и надпись счётчика из `head()`. Каждая строка,
// которую эти функции производят, сверяется с байтом борда машинно —
// `src/render/__parity__/wizard-s2-copy.test.jsx`.
//
// ПОЧЕМУ ОТДЕЛЬНЫЙ МОДУЛЬ, А НЕ ТЕЛО КОМПОНЕНТА. Здесь модель А в её экранной форме (`s484.d1`):
// конец этапа НЕ хранится — он берётся у соседа сверху. Это утверждение о данных, а не о
// разметке, и проверять его надо без монтирования: иначе первый же тест на границу этапа
// потянет за собой провайдер, оболочку и jsdom.
//
// ⚠️ ЧАСЫ СЮДА НЕ ЗАХОДЯТ, И ЭТО ГЛАВНОЕ ОТЛИЧИЕ ОТ БОРДА. У борда `nowMo()` читает
// `new Date()` — он мокап, ему можно. У нас «сейчас» приходит из конверта (`session.nowY`,
// захвачен один раз на сессию `captureNowY`). Ловушка названа в `_CC-INDEX.md` шага 2 прямо:
// иначе длительность текущего этапа поедет между сборкой паспорта и его чтением.
//
// ⭐ РАЗРЕШЕНИЕ ПОДНЯТО ДО МЕСЯЦА (`s898.d1`, B4-2). Здесь стояла оговорка, что `nowY` — целый
// год и текущий этап считается до ЯНВАРЯ, с недосчётом до одиннадцати месяцев; она больше не
// верна. Конверт хранит «сейчас» десятичным годом с месяцем, и `nowMonths` читает из него месяц,
// а не подставляет январь. Согласие с движком при этом не потеряно — оно и было причиной брать
// сторону конверта: `endY` последнего этапа = тот же `nowY` (`toWizardInput.js`).

import { MAX_STAGES } from '../../contracts.js';
import { isMonthYear } from '../session.js';

/** Пол гейта (`s461`, борд `FLOOR`). Надпись счётчика здесь, принуждение — `steps/stageGate.js` (B4-4). */
export const STAGE_FLOOR = 3;

/**
 * Порог «подробно» (борд: `DEEP`). Здесь — ветки бейджа и тон кнопки `add`; от него же считают
 * счётная микрокопи и шит-нудж, и они живут в `steps/stageGate.js` (B4-4, приземлено).
 */
export const STAGE_DEEP = 5;

/**
 * Порог плотного режима (борд v6: `(S.n+(S.draft?1:0))>=FLOOR`) — это ПОЛ ГЕЙТА, а не своё число.
 *
 * ⚠️ БЫЛО `DENSE_OVER = 7` («после седьмого ряда», борд v5: `const dense=S.n>7`). Раскладка v6
 * сдвинула порог на три: плотность — часть экономии высоты на 375×667, ради которой борд и
 * переиздавали, и включаться ей полагается там, где список впервые упирается в бюджет. Отдельной
 * константы больше нет намеренно: два числа с одинаковым значением разъехались бы молча.
 * Порог читается прямо из `STAGE_FLOOR` — см. `isDense` ниже.
 */

/** Потолок (`s336.d4`) берётся у контракта, а не повторяется числом. Принуждение — `commitDraft`. */
export { MAX_STAGES };

/**
 * `MM/YYYY` → порядковый номер месяца (борд: `mo()`).
 * Неразбираемое значение — `null`, а не `NaN`: «не знаю» обязано быть отличимо от числа.
 */
export function monthsOf(mmYYYY) {
  if (typeof mmYYYY !== 'string') return null;
  const m = /^\s*(\d{1,2})\/(\d{4})\s*$/.exec(mmYYYY);
  if (!m) return null;
  const month = Number(m[1]);
  if (month < 1 || month > 12) return null;
  return Number(m[2]) * 12 + (month - 1);
}

/**
 * «Сейчас» в тех же единицах — месяц из конверта (`s898.d1`).
 *
 * ⚠️ `Math.round`, А НЕ `Math.floor` — И ЭТО ЗАЩИТА, А НЕ ПОЧИНКА ЖИВОГО СЛУЧАЯ. Оговорка
 * здесь сначала стояла обратная («иначе `floor` съедал бы месяц через раз»), и замер её
 * опроверг: `(Y + m/12) * 12` в double выходит ТОЧНЫМ целым во всех 2412 случаях 1900–2100,
 * то есть на своих значениях `floor` и `round` неразличимы. Расходятся они только у КРАЯ окна,
 * которое `isMonthYear` впускает намеренно (±1e-6): там `floor` назвал бы сентябрь августом и
 * укоротил текущий этап на месяц — тем же способом, каким целый год укорачивал его на
 * одиннадцать. Своего производителя у таких значений нет; выбор пришпилен тестом.
 *
 * Не-месяц (мусор из devtools) — `null`, а не `NaN`: «не знаю» обязано быть отличимо от числа,
 * как и в `monthsOf`.
 */
export function nowMonths(nowY) {
  return isMonthYear(nowY) ? Math.round(nowY * 12) : null;
}

/**
 * Длительность словами (борд: `dur()`), вместе с полом в один месяц.
 *
 * ⚠️ ПОЛ `Math.max(1, …)` — БАЙТ БОРДА (`:351`), и он же половина `s540.d1`: этап, начатый в
 * текущем месяце, печатает `1m`. Вторая половина решения — запрет заводить проверку
 * `endY === startY` в контракте — это запрет, он исполнен тем, что такой проверки нет.
 */
export function durationLabel(fromMonths, toMonths) {
  if (fromMonths === null || toMonths === null) return null;
  const n = Math.max(1, toMonths - fromMonths);
  const y = Math.floor(n / 12);
  const m = n % 12;
  return `${y ? `${y}y ` : ''}${m || !y ? `${m}m` : ''}`.trim();
}

/**
 * Конец ряда `i` — это старт его СОСЕДА СВЕРХУ (`s484.d1`, борд: `endOf()`).
 *
 * Ряды приходят новым сверху (`s489.d1`), поэтому у нулевого соседа сверху нет: его конец —
 * «сейчас», и здесь это `null`, а не дата. Словом `present` его называет ряд, а не эта функция.
 */
export function endStartOf(rows, i) {
  return i === 0 ? null : (rows[i - 1] && rows[i - 1].start) || null;
}

/** Тег ряда над рамкой (борд: `d.dataset.tag`). */
export function rowTag(i, n) {
  return `stage · ${String(i + 1).padStart(2, '0')} / ${String(n).padStart(2, '0')}`;
}

/**
 * Что печатает один ряд: имя, строку дат и длительность.
 *
 * Длительность `null` — когда дату разобрать нечем. Ряд в этом случае печатает даты без хвоста
 * `· 3y 4m`, а не `NaN`: от визарда такой ряд прийти не может (`s895.d2` — ряд без даты падает
 * громко на эмиссии), но конверт правится руками в devtools, и экран об этом не должен падать.
 */
export function stageRow(rows, i, nowY) {
  const row = rows[i] || {};
  const endStart = endStartOf(rows, i);
  const from = monthsOf(row.start);
  const to = endStart === null ? nowMonths(nowY) : monthsOf(endStart);

  return {
    tag: rowTag(i, rows.length),
    name: row.name || '',
    start: typeof row.start === 'string' ? row.start : '',
    // `present` — слово борда для конца текущего этапа в СОХРАНЁННОМ ряду (`(e||'present')`).
    // Амберное `now` борд печатает только в черновике — это другой объект, и он в B4-2.
    end: endStart === null ? 'present' : endStart,
    duration: durationLabel(from, to),
  };
}

/**
 * Надпись счётчика у разделителя (борд: `head()`).
 *
 * Три ветки борда сохранены дословно, включая `15 · full`. Это по-прежнему ОТЧЁТ о числе рядов:
 * скрытие `add`, `capline` и запрет шестнадцатой записи живут не здесь (`StepTwo.jsx`,
 * `commitDraft`), и надпись считается независимо от них.
 */
export function countLabel(n) {
  if (n < STAGE_FLOOR) return `${n} / ${STAGE_FLOOR}`;
  if (n >= MAX_STAGES) return `${MAX_STAGES} · full`;
  return `${n} stages`;
}

/** Достаточно ли рядов, чтобы счётчик сменил цвет (борд: `S.n>=FLOOR` → `--moss-soft`). */
export function countEnough(n) {
  return n >= STAGE_FLOOR;
}

/**
 * Плотный режим списка (борд v6: `const dense=(S.n+(S.draft?1:0))>=FLOOR`).
 *
 * ⚠️ ЧЕРНОВИК СЧИТАЕТСЯ РЯДОМ, и это часть формулы борда, а не округление: открытый черновик
 * занимает в списке ровно столько же места, сколько сохранённый ряд. Не считать его значило бы
 * переключать плотность в момент `✓` — то есть ровно тогда, когда список и так перестраивается.
 *
 * @param {number} n сохранённых рядов
 * @param {object|null} [draft] открытый черновик, если он есть
 */
export function isDense(n, draft = null) {
  return n + (draft ? 1 : 0) >= STAGE_FLOOR;
}
